import { Typography } from '@mui/material';
import { DateTimePicker, renderMultiSectionDigitalClockTimeView } from '@mui/x-date-pickers';
import { CalendarClock, Clock, Send } from 'lucide-react';
import { hiddenPickerSx } from 'src/components/DatePicker/hidden-picker.styles';
import {
  ModalList,
  type ModalListItem,
  ModalStep,
  useModalStep,
  useModalSteps,
} from 'src/components/ModalContainer';
import { TimeFormats } from 'src/graphql';
import type { ScheduleListItem } from 'src/pages/Conversations/Chat/InputField/hooks/use-schedule-send-modal-state';
import { SmartSendConfirmStep } from 'src/pages/Conversations/Chat/InputField/smart-send/smart-send-confirm-step';
import { useSmartSend } from 'src/pages/Conversations/Chat/InputField/smart-send/smart-send-context';
import { useDisplayTime } from 'src/utils/hooks';

const CUSTOM_ITEM_ID = 'custom';

interface SmartSendOption extends ModalListItem {
  scheduleItem?: ScheduleListItem;
}

interface SmartSendTimeStepProps {
  /** `confirm` opens the confirmation next; `back` returns to the confirmation that opened this step. */
  afterPick: 'confirm' | 'back';
}

/** Step 1: when to send — a typed time, a preset, a custom date, or send now and act. */
export const SmartSendTimeStep = ({ afterPick }: SmartSendTimeStepProps) => {
  const { pushStep } = useModalSteps();
  const { popStep } = useModalStep();
  const { timeFormatPref } = useDisplayTime();
  const { schedule, sendAndCommands, isProcessingOption, selectSendAnd } = useSmartSend();

  const goOn = () => {
    if (afterPick === 'confirm') {
      void pushStep(<SmartSendConfirmStep />);
    } else {
      popStep();
    }
  };

  const options: SmartSendOption[] = [
    ...sendAndCommands.map(({ id, label, description }) => ({
      id,
      title: label,
      description,
      icon: <Send />,
    })),
    ...schedule.listItems.map((item) => ({
      id: item.id,
      title: item.primary,
      icon: item.id === CUSTOM_ITEM_ID ? <CalendarClock /> : <Clock />,
      endAdornment: (
        <Typography variant="caption" color="text.secondary" noWrap>
          {item.secondary}
        </Typography>
      ),
      scheduleItem: item,
    })),
  ];

  const handleSelect = ({ id, scheduleItem }: SmartSendOption) => {
    if (isProcessingOption) {
      return;
    }
    if (!scheduleItem) {
      selectSendAnd(id);
      return;
    }
    scheduleItem.onSelect();
    if (scheduleItem.id !== CUSTOM_ITEM_ID) {
      goOn();
    }
  };

  const handlePickerAccept = () => {
    schedule.commitCustomDate();
    goOn();
  };

  return (
    <ModalStep title="Smart send">
      <ModalList
        items={options}
        shouldFilter={false}
        onSearchChange={schedule.onInputChange}
        onSelect={handleSelect}
        loading={isProcessingOption}
        placeholder={schedule.placeholder}
        emptyText="No matching time recognized."
        testId="smart-send-modal"
      />
      <DateTimePicker
        open={schedule.isPickerOpen}
        onOpen={schedule.openPicker}
        onClose={schedule.closePicker}
        value={schedule.customDate}
        onChange={schedule.setCustomDate}
        onAccept={handlePickerAccept}
        disablePast
        desktopModeMediaQuery="(min-width: 99999px)"
        minutesStep={schedule.minutesStep}
        views={['day', 'hours', 'minutes']}
        viewRenderers={{
          hours: renderMultiSectionDigitalClockTimeView,
          minutes: renderMultiSectionDigitalClockTimeView,
        }}
        ampm={timeFormatPref === TimeFormats.TwelveHour}
        slotProps={{ textField: { sx: { display: 'none' } } }}
        aria-hidden="true"
        sx={hiddenPickerSx}
      />
    </ModalStep>
  );
};
